import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import Swal from 'sweetalert2';
import { AiSuggestedProductDetailsDto } from '../../../models/ai-invoice-voice.model';
import { AddProductEndPoint } from '../../../Data/service/global.service';
import { HttpHelpService } from '../../../Data/service/http-help.service';
import { AddMerchantCategoryModalComponent } from '../add-merchant-category-modal/add-merchant-category-modal.component';
import { companyDisplayName } from '../../../shared/utils/company-display-name';

/**
 * Add-product dialog used from documents. Fields, labels, order, and
 * required rules match the products-page "new product" form.
 */
@Component({
	selector: 'app-add-product-modal',
	templateUrl: './add-product-modal.component.html',
	styleUrls: ['./add-product-modal.component.scss'],
})
export class AddProductComponent implements OnInit, OnChanges {
	@ViewChild('aiPreviewModal') aiPreviewModal: any;

	@Output() productAdded = new EventEmitter<any>();
	@Output() cancel = new EventEmitter<void>();

	@Input() suggestedProductName: string | null = null;
	@Input() suggestedProductDetails: AiSuggestedProductDetailsDto | null = null;
	/** When set (from the open document), the company field is filled and locked. */
	@Input() companyId: number | null = null;

	curentLang = localStorage.getItem('curentLang') || 'ar';
	userRegistId = JSON.parse(localStorage.getItem('user_id'));
	merchantId = JSON.parse(localStorage.getItem('merchant_id'));

	productForm: FormGroup;
	validate = false;
	isLoading = false;

	categoriesData: any[] = [];
	unitMesauereData: any[] = [];
	companiesData: any[] = [];
	merchantCategoriesData: any[] = [];

	currentImageUrl: string | null = null;
	aiPreviewUrl: string | null = null;
	aiPreviewKey: string | null = null;
	aiPrompt = '';
	pendingPreviewKey: string | null = null;
	readonly addCategoryOptionValue = -1;

	private lastCategoryId: number | null = null;

	constructor(
		private spinner: NgxSpinnerService,
		private service: HttpHelpService,
		private modalService: NgbModal,
		private toastr: ToastrService,
	) {
		this.productForm = this.buildFormGroup();
	}

	ngOnInit(): void {
		this.loadDropdowns();
		this.applySuggestedProduct();
		this.applyLockedCompany();
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (!this.productForm) {
			return;
		}
		if (changes['companyId']) {
			this.applyLockedCompany();
		}
		if (changes['suggestedProductName'] || changes['suggestedProductDetails']) {
			this.applySuggestedProduct();
		}
	}

	get companyLocked(): boolean {
		return this.normalizeCompanyId(this.companyId) != null;
	}

	private buildFormGroup(): FormGroup {
		return new FormGroup({
			product_name_en: new FormControl(''),
			product_name_ar: new FormControl(''),
			product_name_he: new FormControl('', Validators.required),
			des_ar: new FormControl(''),
			des_en: new FormControl(''),
			des_he: new FormControl('', Validators.required),
			product_code: new FormControl(''),
			barcode: new FormControl(''),
			company_id: new FormControl(null, Validators.required),
			price: new FormControl(null, Validators.required),
			measurement_unit_id: new FormControl(null, Validators.required),
			categories_id: new FormControl(null),
			merchant_category_id: new FormControl(null),
			show_in_pos: new FormControl(true),
			is_product_active: new FormControl(true),
			pos_sort_order: new FormControl(0),
			generate_ai_image: new FormControl(false),
			ai_image_prompt: new FormControl(''),
			merchant_id: new FormControl(this.merchantId),
			created_by: new FormControl(this.userRegistId),
		});
	}

	private msg(ar: string, en: string, he: string): string {
		return this.curentLang === 'ar' ? ar : this.curentLang === 'en' ? en : he;
	}

	private apiMsg(res: any): string {
		return this.curentLang === 'ar'
			? res.messageAr
			: this.curentLang === 'en'
				? res.messageEn
				: res.messageHe;
	}

	private swal(options: Record<string, unknown>) {
		const customClass = (options['customClass'] as Record<string, string>) || {};
		return Swal.fire({
			...options,
			customClass: { ...customClass, container: 'add-product-swal' },
		});
	}

	private normalizeCompanyId(value: unknown): number | null {
		if (value == null || value === '') {
			return null;
		}
		const n = Number(value);
		return Number.isFinite(n) && n > 0 ? n : null;
	}

	getProductTitle(): string {
		return String(this.productForm.getRawValue().product_name_he || '').trim();
	}

	canGenerateAiImage(): boolean {
		return this.getProductTitle().length > 0;
	}

	aiImageHint(): string {
		return this.msg(
			'أدخل اسم المنتج أولاً. كلما وصف الاسم المنتج بدقة، كانت صورة الذكاء الاصطناعي أدق.',
			'Enter the product name first. The more accurately the title describes the product, the better the AI photo will be.',
			'הזן תחילה את שם המוצר. ככל שהשם מתאר את המוצר בדיוק רב יותר, תמונת ה-AI תהיה מדויקת יותר.',
		);
	}

	addCategoryLabel(): string {
		return this.msg('إضافة تصنيف', 'Add category', 'הוסף קטגוריה');
	}

	onMerchantCategoryChange(value: number | null): void {
		if (value === this.addCategoryOptionValue) {
			const revert =
				this.lastCategoryId ??
				(this.merchantCategoriesData[0]
					? this.merchantCategoryPk(this.merchantCategoriesData[0])
					: null);
			setTimeout(() => {
				this.productForm.patchValue({ merchant_category_id: revert }, { emitEvent: false });
			});
			this.openAddCategoryModal();
			return;
		}
		if (value != null) {
			this.lastCategoryId = value;
		}
	}

	openAddCategoryModal(): void {
		const ref = this.modalService.open(AddMerchantCategoryModalComponent, {
			size: 'xl',
			centered: true,
			windowClass: 'add-product-nested-modal',
			backdropClass: 'add-product-nested-backdrop',
		});
		ref.result.then(
			(created) => {
				const newId = this.merchantCategoryPk(created);
				this.reloadMerchantCategories(newId || undefined);
			},
			() => undefined,
		);
	}

	private reloadMerchantCategories(selectId?: number): void {
		this.service.getActiveMerchantProductCategories().subscribe({
			next: (res) => {
				this.merchantCategoriesData = Array.isArray(res)
					? res
					: res?.data || res?.categories || [];

				let idToSelect = selectId;
				if (idToSelect && !this.merchantCategoriesData.some((c) => this.merchantCategoryPk(c) === idToSelect)) {
					idToSelect = undefined;
				}
				if (!idToSelect && this.merchantCategoriesData.length) {
					idToSelect = this.merchantCategoryPk(
						this.merchantCategoriesData[this.merchantCategoriesData.length - 1],
					);
				}
				if (idToSelect) {
					this.productForm.patchValue({ merchant_category_id: idToSelect });
					this.lastCategoryId = idToSelect;
				}
			},
			error: () => {
				this.merchantCategoriesData = [];
			},
		});
	}

	private applyDefaultMeasurementUnit(): void {
		const current = this.productForm.getRawValue().measurement_unit_id;
		if (current) return;
		if (!this.unitMesauereData?.length) return;
		this.productForm.patchValue({
			measurement_unit_id: Number(this.unitMesauereData[0].measurement_unit_id),
		});
	}

	private applyDefaultCategory(): void {
		const current = this.productForm.getRawValue().merchant_category_id;
		if (current != null && current !== this.addCategoryOptionValue) {
			this.lastCategoryId = current;
			return;
		}
		if (!this.merchantCategoriesData?.length) return;
		const firstId = this.merchantCategoryPk(this.merchantCategoriesData[0]);
		this.productForm.patchValue({ merchant_category_id: firstId });
		this.lastCategoryId = firstId;
	}

	private applyDefaultGeneralCategory(): void {
		const current = this.productForm.getRawValue().categories_id;
		if (current != null) return;
		if (!this.categoriesData?.length) return;
		const firstId = Number(this.categoriesData[0]?.categories_id);
		if (!firstId) return;
		this.productForm.patchValue({ categories_id: firstId });
	}

	private applyDefaultCompany(): void {
		const locked = this.normalizeCompanyId(this.companyId);
		if (locked) {
			this.productForm.patchValue({ company_id: locked });
			this.productForm.get('company_id')?.disable({ emitEvent: false });
			return;
		}
		this.productForm.get('company_id')?.enable({ emitEvent: false });
		if (this.productForm.getRawValue().company_id) return;
		if (this.companiesData.length !== 1) return;
		const companyId = Number(this.companiesData[0]?.company_id);
		if (!companyId) return;
		this.productForm.patchValue({ company_id: companyId });
	}

	private applyLockedCompany(): void {
		const locked = this.normalizeCompanyId(this.companyId);
		if (!locked) {
			this.productForm.get('company_id')?.enable({ emitEvent: false });
			return;
		}
		this.productForm.patchValue({ company_id: locked });
		this.productForm.get('company_id')?.disable({ emitEvent: false });
	}

	private applySuggestedProduct(): void {
		const details = this.suggestedProductDetails;
		const name = details?.name?.trim() || this.suggestedProductName?.trim() || '';
		const patch: Record<string, unknown> = {};
		if (name) {
			patch['product_name_he'] = name;
			patch['product_name_ar'] = name;
			patch['product_name_en'] = name;
		}
		if (details?.unit_price != null && Number.isFinite(details.unit_price)) {
			patch['price'] = details.unit_price;
		}
		if (details?.description?.trim()) {
			patch['des_he'] = details.description.trim().slice(0, 600);
			patch['des_ar'] = patch['des_he'];
			patch['des_en'] = patch['des_he'];
		}
		if (Object.keys(patch).length) {
			this.productForm.patchValue(patch);
		}
	}

	merchantCategoryPk(item: any): number {
		return Number(item?.merchant_category_id ?? item?.id);
	}

	merchantCategoryLabel(item: any): string {
		if (!item) return '';
		return this.curentLang === 'en'
			? item.name_en
			: this.curentLang === 'ar'
				? item.name_ar
				: item.name_he;
	}

	selectedMerchantCategoryImageUrl(): string | null {
		const id = this.productForm?.getRawValue()?.merchant_category_id;
		if (id == null || id === this.addCategoryOptionValue) return null;
		const item = this.merchantCategoriesData.find(
			(c) => this.merchantCategoryPk(c) === Number(id),
		);
		const url = item?.image_url;
		return url ? String(url) : null;
	}

	companyLabel(item: any): string {
		return companyDisplayName(item, this.curentLang);
	}

	measurementUnitLabel(item: any): string {
		return this.curentLang === 'en'
			? item?.measurement_unit_name_en
			: this.curentLang === 'ar'
				? item?.measurement_unit_name_ar
				: item?.measurement_unit_name_he;
	}

	generalCategoryLabel(item: any): string {
		if (!item) return '';
		return this.curentLang === 'en'
			? item.categories_name_en
			: this.curentLang === 'ar'
				? item.categories_name_ar
				: item.categories_name_he;
	}

	loadDropdowns(): void {
		this.service.getAllActiveCategories().subscribe({
			next: (res) => {
				this.categoriesData = res;
				this.applyDefaultGeneralCategory();
			},
		});
		this.service.getAllActiveMeasurementUnits().subscribe({
			next: (res) => {
				this.unitMesauereData = res;
				this.applyDefaultMeasurementUnit();
			},
		});
		this.service.getAllActiveCompanyByMerchant().subscribe({
			next: (res) => {
				const list = Array.isArray(res) ? res : res?.data || [];
				this.companiesData = list.map((item: any) => ({
					...item,
					company_id: Number(item?.company_id),
				}));
				this.applyDefaultCompany();
			},
			error: () => (this.companiesData = []),
		});
		this.service.getActiveMerchantProductCategories().subscribe({
			next: (res) => {
				this.merchantCategoriesData = Array.isArray(res)
					? res
					: res?.data || res?.categories || [];
				this.applyDefaultCategory();
			},
			error: () => (this.merchantCategoriesData = []),
		});
	}

	private buildPosFields(formValue: any) {
		const companyId = formValue.company_id ? Number(formValue.company_id) : null;
		const productCode = String(formValue.product_code || '').trim();
		const barcode = String(formValue.barcode || '').trim();
		const rawCategoryId = formValue.merchant_category_id;
		const merchantCategoryId =
			rawCategoryId != null && Number(rawCategoryId) > 0 ? Number(rawCategoryId) : null;
		return {
			product_code: productCode || undefined,
			barcode: barcode || undefined,
			company_id: companyId || undefined,
			merchant_category_id: merchantCategoryId || undefined,
			show_in_pos: formValue.show_in_pos !== false,
			pos_sort_order: Number(formValue.pos_sort_order) || 0,
			auto_generate_sku: null,
			auto_generate_barcode: null,
			generate_ai_image: !!formValue.generate_ai_image,
			ai_image_prompt: String(formValue.ai_image_prompt || '').trim() || undefined,
		};
	}

	private buildImageSaveFields(formValue: any) {
		const fields: any = {};
		if (this.pendingPreviewKey) {
			fields.preview_key = this.pendingPreviewKey;
		} else if (formValue.generate_ai_image) {
			fields.generate_ai_image = true;
			const prompt = String(formValue.ai_image_prompt || this.aiPrompt || '').trim();
			if (prompt) fields.ai_image_prompt = prompt;
		}
		return fields;
	}

	addedData(): void {
		this.save();
	}

	save(): void {
		const formValue = this.productForm.getRawValue();
		const productNameHe = String(formValue.product_name_he || '').trim();
		const price = Number(formValue.price);
		const desHe = String(formValue.des_he || '').trim();
		const measurementUnitId = Number(formValue.measurement_unit_id) || 0;
		const companyId = Number(formValue.company_id) || 0;

		if (!productNameHe || !price || !desHe || !measurementUnitId || !companyId) {
			this.swal({
				icon: 'warning',
				title: this.msg(
					'يرجى ملء الحقول المطلوبة',
					'Please fill out the required fields',
					'אנא מלא את השדות הנדרשים',
				),
			});
			this.productForm.markAllAsTouched();
			this.validate = !this.validate;
			return;
		}

		this.productForm.patchValue({
			product_name_ar: productNameHe,
			product_name_en: productNameHe,
			des_ar: formValue.des_he,
			des_en: formValue.des_he,
		});

		const savedForm = this.productForm.getRawValue();
		const pos = this.buildPosFields(savedForm);
		const imageFields = this.buildImageSaveFields(savedForm);

		const model: any = {
			product_name_en: productNameHe,
			product_name_ar: productNameHe,
			product_name_he: productNameHe,
			product_code: pos.product_code || '',
			barcode: pos.barcode,
			company_id: pos.company_id,
			price,
			des_ar: savedForm.des_he || '',
			des_en: savedForm.des_he || '',
			des_he: savedForm.des_he || '',
			measurement_unit_id: measurementUnitId,
			categories_id: savedForm.categories_id || null,
			merchant_category_id: pos.merchant_category_id,
			show_in_pos: pos.show_in_pos,
			pos_sort_order: pos.pos_sort_order,
			auto_generate_sku: pos.auto_generate_sku,
			auto_generate_barcode: pos.auto_generate_barcode,
			quantity_limited: false,
			has_options: false,
			...imageFields,
			merchant_id: Number(this.merchantId),
			created_by: Number(this.userRegistId),
		};

		this.isLoading = true;
		this.spinner.show();
		this.service.Post(AddProductEndPoint.POST, model).subscribe({
			next: (res) => this.handleSaveResponse(res, model),
			error: () => {
				this.isLoading = false;
				this.spinner.hide();
				this.swal({
					icon: 'error',
					title: this.msg('فشل إضافة المنتج', 'Failed to add product', 'הוספת המוצר נכשלה'),
				});
			},
		});
	}

	private handleSaveResponse(res: any, model: any): void {
		if (res.status === 200) {
			const saved = res.data || {};
			const savedId = saved.product_id || res.product_id;
			const desiredStatus = this.productForm.getRawValue().is_product_active ? 1 : 59;

			const finish = () => {
				this.isLoading = false;
				this.spinner.hide();
				const message = this.apiMsg(res);
				if (message) {
					this.toastr.success(message);
				}
				this.productAdded.emit(this.buildAddedProduct(saved, model, savedId));
				this.cancel.emit();
			};

			if (savedId && desiredStatus === 59) {
				this.service
					.changeProductStatus(savedId, {
						product_status: 59,
						updated_by: this.userRegistId,
					})
					.subscribe({ next: () => finish(), error: () => finish() });
			} else {
				finish();
			}
			return;
		}

		this.isLoading = false;
		this.spinner.hide();
		if (res.status === 400 || res.status === 403 || res.status === 500) {
			this.swal({ icon: 'error', title: this.apiMsg(res) });
			return;
		}
		this.swal({
			icon: 'error',
			title: this.msg('فشل إضافة المنتج', 'Failed to add product', 'הוספת המוצר נכשלה'),
		});
	}

	private buildAddedProduct(saved: any, model: any, savedId: number): any {
		const description = model.des_he || saved[`des_${this.curentLang}`] || saved.des_he || '';
		const itemName = model.product_name_he || saved[`product_name_${this.curentLang}`] || '';
		return {
			...saved,
			...model,
			product_id: savedId,
			product_name_en: model.product_name_en,
			product_name_ar: model.product_name_ar,
			product_name_he: model.product_name_he,
			des_en: model.des_en,
			des_ar: model.des_ar,
			des_he: model.des_he,
			price: model.price,
			price_per_unit: model.price,
			catalog_id: model.product_code || '',
			description,
			item_name: itemName,
			quantity: 1,
			discount: 0,
			vat_rate: 0,
			product_status: this.productForm.getRawValue().is_product_active ? 1 : 59,
		};
	}

	onCancel(): void {
		this.cancel.emit();
	}

	triggerImageUpload(input: HTMLInputElement): void {
		input.click();
	}

	onProductImageSelected(_event: Event): void {
		const input = _event.target as HTMLInputElement;
		input.value = '';
		this.swal({
			icon: 'info',
			title: this.msg(
				'احفظ المنتج أولاً ثم ارفع الصورة',
				'Save the product first, then upload the image',
				'שמור את המוצר תחילה ואז העלה את התמונה',
			),
		});
	}

	generateAiImage(): void {
		const productName = this.getProductTitle();
		if (!productName) {
			this.swal({
				icon: 'warning',
				title: this.msg(
					'يرجى إدخال اسم المنتج قبل توليد الصورة',
					'Please enter the product name before generating an image',
					'יש להזין את שם המוצר לפני יצירת תמונה',
				),
				text: this.aiImageHint(),
			});
			this.productForm.get('product_name_he')?.markAsTouched();
			return;
		}

		const prompt =
			String(this.productForm.getRawValue().ai_image_prompt || this.aiPrompt || '').trim() || undefined;

		this.spinner.show();
		this.service
			.aiGenerateProductImage({
				merchant_id: Number(this.merchantId),
				prompt,
				product_name: productName,
			})
			.subscribe({
				next: (res) => {
					this.spinner.hide();
					const data = res.data || res;
					this.aiPreviewUrl = data.preview_url || null;
					this.aiPreviewKey = data.preview_key || null;
					if (!this.aiPreviewUrl || !this.aiPreviewKey) {
						this.swal({
							icon: 'error',
							title:
								this.apiMsg(res) ||
								this.msg('فشل توليد الصورة', 'AI image generation failed', 'יצירת התמונה נכשלה'),
						});
						return;
					}
					this.modalService.open(this.aiPreviewModal, {
						size: 'lg',
						centered: true,
						windowClass: 'add-product-nested-modal',
						backdropClass: 'add-product-nested-backdrop',
					});
				},
				error: () => {
					this.spinner.hide();
					this.swal({
						icon: 'error',
						title: this.msg('فشل توليد الصورة', 'AI image generation failed', 'יצירת התמונה נכשלה'),
					});
				},
			});
	}

	approveAiImage(modal?: any): void {
		if (!this.aiPreviewKey) return;
		this.pendingPreviewKey = this.aiPreviewKey;
		this.currentImageUrl = this.aiPreviewUrl;
		this.productForm.patchValue({ generate_ai_image: false });
		this.aiPreviewUrl = null;
		this.aiPreviewKey = null;
		if (modal) modal.close();
		this.swal({
			icon: 'success',
			title: this.msg(
				'تم تجهيز الصورة — احفظ المنتج لاعتمادها',
				'Image ready — save the product to apply it',
				'התמונה מוכנה — שמור את המוצר כדי להחיל אותה',
			),
		});
	}
}
